import { activeRouteStore, getAttemptedTransitionUrl, setAttemptedTransitionUrl } from '@customerio/hydra-nav-state';
import { useStore } from '@nanostores/react';
import { redirect, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { getSampleAccounts } from '@/features/workspaces';
import Journeys, { JourneysNonBackgroundApp } from '@/pages/journeys';
import { useAuthQuery } from '@/services/auth';
import { useLocation, useParams } from '@/services/router/compat';
import { generatePath } from '@/services/router/generate-path';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { AppBundle, AppBundleRoutes } from '@/utils/app-bundles';
import type { AppBundle as AppBundleType } from '@/utils/app-bundles';
import { sentryCaptureLegacyRedirect } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import { queryClient } from '@/layouts/root';

export function JourneysWithAuthRefresh() {
  const auth = useAuthQuery();
  const location = useLocation();
  const activeRoute = useStore(activeRouteStore);

  useEffect(() => {
    if (auth.data == null && getAttemptedTransitionUrl() == null && window.location.pathname.startsWith('/login')) {
      setAttemptedTransitionUrl(window.location.origin);
    }
    // oxlint-disable-next-line react/exhaustive-deps -- Only needs to re-run when location changes
  }, [location]);

  useEffect(() => {
    if (!isEmptyString(activeRoute) && location.pathname !== window.location.pathname && !auth.isFetching) {
      auth.refetch();
    }
    // oxlint-disable-next-line react/exhaustive-deps -- refetch is stable; listing `auth` here re-runs the effect on every query-state change (including the refetch above), causing a login loop that reboots the embedded Journeys app on /login (JOURNEYS-UI-11HZ)
  }, [activeRoute, location]);

  return <JourneysNonBackgroundApp />;
}

export const preHydraJourneysEnvRegex = /^\/env\/(last|(\d+))((\/.*$)|$)/;
export const v1HydraJourneysRegex = /^\/journeys\/env\/(last|(\d+))((\/.*$)|$)/;
export const oldParcelRegex = /^\/workspaces\/(\d+)\/parcel((\/.*$)|$)/;

export function PipelinesLegacyRedirect() {
  const params = useParams({ strict: false });
  const splat = params._splat ?? '';
  const target =
    generatePath('/workspaces/:workspaceId/pipelines', { workspaceId: params.workspaceId ?? '' }) +
    (splat.length > 0 ? ['/', splat].join('') : '');
  return <SafeNavigate to={target} options={{ shouldReplaceHistoryEntry: true }} />;
}

type RedirectRegexToTargetAppProps = { regex: RegExp; targetApp: AppBundleType };

type GetRedirectRegexTargetOptions = RedirectRegexToTargetAppProps & {
  pathname: string;
  search: string;
};

export function getRedirectRegexTarget({ regex, targetApp, pathname, search }: GetRedirectRegexTargetOptions) {
  let path = pathname;
  const matches = pathname.match(regex);

  if (matches != null) {
    const [, workspaceId, _, rest] = matches;
    let adjustedRest = rest != null ? rest.replace(/^\//, '') : '';
    path = generatePath('/workspaces/:workspaceId', { workspaceId });

    if (targetApp === AppBundle.DesignStudio && adjustedRest.startsWith('templates')) {
      adjustedRest = 'dashboard';
    }

    path = adjustedRest.startsWith('settings')
      ? [path, adjustedRest].join('/')
      : [path, AppBundleRoutes[targetApp].replace(/^\//, ''), adjustedRest].filter(Boolean).join('/');

    path = `${path}${search}`;
  } else {
    path = generatePath('/workspaces', {}) + search;
  }

  return path;
}

export function RedirectRegexToTargetApp({ regex, targetApp }: RedirectRegexToTargetAppProps) {
  const path = getRedirectRegexTarget({
    regex,
    targetApp,
    pathname: window.location.pathname,
    search: window.location.search,
  });

  return <SafeNavigate to={path} options={{ shouldReplaceHistoryEntry: true }} />;
}

type LegacyRedirectReporterProps = { children: ReactNode };

export function LegacyRedirectReporter({ children }: LegacyRedirectReporterProps) {
  const location = useLocation();
  const params = useParams({ strict: false });

  const genericPath = Object.entries(params).reduce((accumulator, [key, value]) => {
    if (value == null) {
      return accumulator;
    }

    return accumulator.replace(String(value), key);
  }, location.pathname);

  useEffect(() => {
    sentryCaptureLegacyRedirect(genericPath);
  }, [genericPath]);

  return children;
}

export function legacyRedirect(to: string) {
  return ({ params }: { params: Record<string, string | undefined> }) => {
    const [pathname = '', search = ''] = to.replace(/:(\w+)/g, (_, key) => params[key] ?? '').split('?');
    throw redirect({
      to: pathname,
      search: search.length > 0 ? Object.fromEntries(new URLSearchParams(search)) : true,
    });
  };
}

type GetLegacyWorkspaceSettingsRedirectTargetOptions = {
  search: string;
  splat?: string;
  workspaceId: string;
};

export function getLegacyWorkspaceSettingsRedirectTarget({
  search,
  splat = '',
  workspaceId,
}: GetLegacyWorkspaceSettingsRedirectTargetOptions) {
  const settingsPath = generatePath('/workspaces/:workspaceId/settings', { workspaceId });
  const normalizedSplat = splat.replace(/^\/+/, '');
  const pathname = [settingsPath, normalizedSplat].filter(Boolean).join('/');

  return `${pathname}${search}`;
}

export function LegacyWorkspaceSettingsRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const target = getLegacyWorkspaceSettingsRedirectTarget({
    search: location.search,
    splat: params._splat,
    workspaceId: params.workspaceId ?? '',
  });

  useEffect(() => {
    void navigate({ to: target as never, replace: true });
  }, [navigate, target]);

  return null;
}

export async function sampleWorkspaceRedirect() {
  const { workspaceId } = await queryClient.fetchQuery({
    queryKey: ['sampleAccounts'],
    queryFn: getSampleAccounts,
  });

  throw redirect({ to: generatePath('/workspaces/:workspaceId/journeys', { workspaceId }) });
}

export function JourneysLegacyRoute() {
  return (
    <LegacyRedirectReporter>
      <JourneysNonBackgroundApp />
    </LegacyRedirectReporter>
  );
}

export function JourneysLegacyAuthRefreshRoute() {
  return (
    <LegacyRedirectReporter>
      <JourneysWithAuthRefresh />
    </LegacyRedirectReporter>
  );
}

export function LogoutRoute() {
  return (
    <LegacyRedirectReporter>
      <Journeys />
    </LegacyRedirectReporter>
  );
}
